import { submitLead } from "../forms/submit-lead";
import React from "react";
import { Render } from "@puckeditor/core";
import { CheckCircle2, XCircle } from "lucide-react";
import { cn } from "../lib/utils";
import { getWindowJobsiteConfig } from "../types/runtime-config";
import { useTurnstile } from "../hooks/use-turnstile";
import { uploadFormImage } from "../components/booking-widget/booking-api";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  coreFieldKeys,
  missingRequiredFromData,
  splitCoreValues,
  type Data,
  type FieldValue,
} from "./form-data";
import { contactFormsPuckConfig } from "./form-fields.config";
import { FormValuesProvider } from "./form-values-context";

/**
 * The business's saved contact form, loaded live from the API (the same
 * dynamic-read pattern the booking widget uses, so publishing a form change
 * never needs a site rebuild). `data: null` once settled means "render your
 * built-in default": no saved row, no runtime config, or a fetch failure — a
 * form must never break over the read. While `loading`, surfaces hold the
 * field area with a skeleton instead of the default, so a saved form never
 * flashes the old markup before swapping in.
 */
export function useSavedForm(
  formKey: string | undefined,
  enabled = true,
): { loading: boolean; data: Data | null } {
  const active = enabled && !!formKey;
  const [state, setState] = React.useState<{ loading: boolean; data: Data | null }>({
    loading: active,
    data: null,
  });
  React.useEffect(() => {
    if (!active) return;
    const config = getWindowJobsiteConfig();
    if (!config?.apiUrl || !config?.siteId) {
      setState({ loading: false, data: null });
      return;
    }
    let alive = true;
    setState({ loading: true, data: null });
    fetch(`${config.apiUrl}/api/sites/${config.siteId}/forms/${formKey}`)
      .then((res) => (res.ok ? (res.json() as Promise<{ data?: Data | null }>) : null))
      .then((body) => {
        if (alive) setState({ loading: false, data: body?.data ?? null });
      })
      .catch(() => {
        if (alive) setState({ loading: false, data: null });
      });
    return () => {
      alive = false;
    };
  }, [active, formKey]);
  return state;
}

export function useSavedContactForm(enabled: boolean): { loading: boolean; data: Data | null } {
  return useSavedForm("contact", enabled);
}

/** Silhouette shown while the saved form resolves — field blocks + a button. */
export const FormEmbedSkeleton: React.FC<{ fields?: number; className?: string }> = ({
  fields = 4,
  className,
}) => (
  <div aria-hidden className={cn("jobsite:animate-pulse jobsite:space-y-4", className)}>
    {Array.from({ length: fields }, (_, i) => (
      <div key={i} className="jobsite:space-y-1.5">
        <div className="jobsite:h-3 jobsite:w-24 jobsite:rounded jobsite:bg-muted" />
        <div className="jobsite:h-12 jobsite:w-full jobsite:rounded-md jobsite:bg-muted" />
      </div>
    ))}
    <div className="jobsite:h-12 jobsite:w-full jobsite:rounded-md jobsite:bg-muted" />
  </div>
);

export interface FormEmbedProps {
  /** The saved form's Puck document. */
  data: Data;
  submitLabel?: string;
  successMessage?: string;
  /** Tag stored on formData.source (e.g. "hero-form"); omitted when unset. */
  source?: string;
  /** Which form this is (user-created keys). The server validates it and
   *  filters customFields against that form; contact needs no tag. */
  formKey?: string;
  /** Preset tag sent as the body's formType (analytics parity); omitted when unset. */
  formType?: string;
  hasTrackingPhone?: boolean;
  /** Canonical slug for photo-upload URLs. */
  businessSlug?: string;
  buttonClassName?: string;
  /** Builder canvas slot: replaces the field area with an editable Puck canvas. */
  renderFields?: () => React.ReactNode;
  /** Preview contexts: render everything but never POST. */
  disableSubmit?: boolean;
  className?: string;
}

/**
 * Renders a saved contact form end-to-end: the shared field renderer, required
 * gating, Turnstile, submit to the site's contact endpoint (core values on
 * canonical top-level keys, the rest under customFields), and success/error
 * states. Every surface that shows the business's form embeds THIS — the
 * jobsite ContactForm, the hero lead card, and whatever comes next — so the
 * form renders and submits identically everywhere.
 */
export const FormEmbed: React.FC<FormEmbedProps> = ({
  data,
  submitLabel = "Send Message",
  successMessage = "Thank you! We'll be in touch soon.",
  source,
  formKey,
  formType,
  hasTrackingPhone,
  businessSlug,
  buttonClassName,
  renderFields,
  disableSubmit,
  className,
}) => {
  const [formState, setFormState] = React.useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [values, setValues] = React.useState<Record<string, FieldValue>>({});
  const {
    tokenRef: turnstileTokenRef,
    containerRef: turnstileContainerRef,
    containerClassName: turnstileClassName,
  } = useTurnstile();

  const windowConfig = getWindowJobsiteConfig();
  const uploadImage =
    windowConfig?.apiUrl && businessSlug
      ? async (file: File) => {
          const res = await uploadFormImage(windowConfig.apiUrl!, businessSlug, file);
          return res.ok ? res.url : null;
        }
      : undefined;

  // The builder canvas renders Puck's own fields; gating there is Puck's job.
  const incomplete = !renderFields && missingRequiredFromData(data, values).length > 0;
  const showsPhoneField = coreFieldKeys(data).has("phone");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disableSubmit) return;
    if (missingRequiredFromData(data, values).length > 0) return;

    const config = getWindowJobsiteConfig();
    const turnstileToken = turnstileTokenRef.current;
    if (config?.turnstileSiteKey && !turnstileToken) {
      setFormState("error");
      return;
    }

    setFormState("submitting");

    try {
      if (config?.apiUrl && config?.siteId) {
        const params = new URLSearchParams(window.location.search);
        const isPreview = params.get("preview") === "true";
        const sessionId = isPreview ? null : sessionStorage.getItem("sp_session_id") || null;

        const { core, custom } = splitCoreValues(values);
        const result = await submitLead({
          apiUrl: config.apiUrl,
          siteId: config.siteId,
          backupApiUrl: config.backupApiUrl,
          businessName: config.business?.name,
          payload: {
            ...core,
            ...(Object.keys(custom).length ? { customFields: custom } : {}),
            ...(source ? { source } : {}),
            ...(formKey && formKey !== "contact" ? { formKey } : {}),
            ...(formType ? { formType } : {}),
            variant: config?.variant,
            sessionId,
            ...(turnstileToken ? { turnstileToken } : {}),
          },
        });
        if (result.status === "failed") {
          throw new Error("Lead submission failed");
        }
      }

      setFormState("success");
      setValues({});
    } catch {
      setFormState("error");
      window.setTimeout(() => setFormState("idle"), 3000);
    }
  };

  // Success is terminal: the form is replaced by the confirmation (no append
  // shift below the button, and no double submits).
  if (formState === "success") {
    return (
      <div className={className}>
        <Alert className="jobsite:rounded-2xl jobsite:border-emerald-200 jobsite:bg-emerald-50">
          <CheckCircle2 className="jobsite:h-5 jobsite:w-5 jobsite:text-emerald-600" />
          <AlertDescription className="jobsite:text-base jobsite:font-medium jobsite:text-emerald-700">
            {successMessage}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={cn("jobsite:space-y-4", className)}>
      {renderFields ? (
        renderFields()
      ) : (
        <FormValuesProvider
          values={values}
          onChange={(fieldId, value) => setValues((prev) => ({ ...prev, [fieldId]: value }))}
          uploadImage={uploadImage}
          googleMapsApiKey={windowConfig?.googleMapsApiKey}
        >
          <Render config={contactFormsPuckConfig} data={data} />
        </FormValuesProvider>
      )}

      {/* Turnstile widget container — CF JS API renders into this div after mount */}
      <div ref={turnstileContainerRef} className={turnstileClassName} />

      <Button
        type="submit"
        disabled={formState === "submitting" || incomplete}
        className={cn("jobsite:h-12 jobsite:w-full jobsite:font-semibold", buttonClassName)}
        size="lg"
      >
        {formState === "submitting" ? "Sending..." : submitLabel}
      </Button>

      {showsPhoneField && hasTrackingPhone && (
        <p className="jobsite:text-[11px] jobsite:leading-relaxed jobsite:text-muted-foreground/70">
          By providing your phone number, you consent to receive calls and text messages (SMS). You
          can unsubscribe at any time by replying STOP. Message and data rates may apply.
        </p>
      )}

      {formState === "error" && (
        <Alert className="jobsite:rounded-2xl jobsite:border-destructive/20 jobsite:bg-destructive/10">
          <XCircle className="jobsite:h-5 jobsite:w-5 jobsite:text-destructive" />
          <AlertDescription className="jobsite:text-base jobsite:font-medium jobsite:text-destructive">
            Something went wrong. Please try again.
          </AlertDescription>
        </Alert>
      )}
    </form>
  );
};
